import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, Building2, MapPin, CheckCircle2, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const US_STATES = [
  ["NC", "North Carolina"], ["AL", "Alabama"], ["AK", "Alaska"], ["AZ", "Arizona"], ["AR", "Arkansas"],
  ["CA", "California"], ["CO", "Colorado"], ["CT", "Connecticut"], ["DE", "Delaware"], ["FL", "Florida"],
  ["GA", "Georgia"], ["HI", "Hawaii"], ["ID", "Idaho"], ["IL", "Illinois"], ["IN", "Indiana"], ["IA", "Iowa"],
  ["KS", "Kansas"], ["KY", "Kentucky"], ["LA", "Louisiana"], ["ME", "Maine"], ["MD", "Maryland"],
  ["MA", "Massachusetts"], ["MI", "Michigan"], ["MN", "Minnesota"], ["MS", "Mississippi"], ["MO", "Missouri"],
  ["MT", "Montana"], ["NE", "Nebraska"], ["NV", "Nevada"], ["NH", "New Hampshire"], ["NJ", "New Jersey"],
  ["NM", "New Mexico"], ["NY", "New York"], ["ND", "North Dakota"], ["OH", "Ohio"], ["OK", "Oklahoma"],
  ["OR", "Oregon"], ["PA", "Pennsylvania"], ["RI", "Rhode Island"], ["SC", "South Carolina"], ["SD", "South Dakota"],
  ["TN", "Tennessee"], ["TX", "Texas"], ["UT", "Utah"], ["VT", "Vermont"], ["VA", "Virginia"],
  ["WA", "Washington"], ["WV", "West Virginia"], ["WI", "Wisconsin"], ["WY", "Wyoming"], ["DC", "Washington D.C."],
];

const addressOf = (r) =>
  [r.physicalAddressStreet, r.physicalAddressCity, r.physicalAddressState, r.physicalAddressZip].filter(Boolean).join(", ");
const agentAddressOf = (r) =>
  [r.agentStreet, r.agentCity, r.agentState, r.agentZip].filter(Boolean).join(", ");

export function BusinessSearch({ onSelect, defaultQuery = "", defaultState = "NC" }) {
  const [query, setQuery] = useState(defaultQuery);
  const [stateCode, setStateCode] = useState(defaultState);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);
  const [message, setMessage] = useState("");
  const [retryId, setRetryId] = useState(null);
  const [searched, setSearched] = useState(false);

  const run = async (useRetry = false) => {
    if (!useRetry && query.trim().length < 3) {
      toast.error("Enter at least 3 characters of the business name.");
      return;
    }
    setBusy(true);
    setMessage("");
    try {
      const payload = useRetry && retryId
        ? { retry_id: retryId, state: stateCode }
        : { query: query.trim(), state: stateCode };
      const { data } = await api.post("/business/search", payload);
      setSearched(true);
      if (String(data.status).toLowerCase() === "pending" && data.retryId) {
        setRetryId(data.retryId);
        setResults(data.results || []);
        setMessage("Still searching the state records — this can take a moment. Click “Check again”.");
      } else {
        setRetryId(null);
        setResults(data.results || []);
        setMessage((data.results || []).length ? "" : "No matching businesses found. Try a different name or state.");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Business lookup failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="business-search">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
          <Input value={query} onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && run(false)}
            data-testid="business-search-query" placeholder="Business / entity name"
            className="rounded-sm h-11 pl-9" />
        </div>
        <select value={stateCode} onChange={(e) => setStateCode(e.target.value)}
          data-testid="business-search-state"
          className="h-11 rounded-sm border-2 border-black/15 bg-background px-3 text-sm sm:w-56">
          {US_STATES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
        </select>
        <Button onClick={() => run(false)} disabled={busy} data-testid="business-search-btn"
          className="rounded-sm h-11 gap-2 bg-primary hover:bg-accent hover:text-accent-foreground transition-colors">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} Search
        </Button>
      </div>

      {message && (
        <div className="mt-4 flex items-center gap-3 text-sm text-muted-foreground" data-testid="business-search-message">
          <span>{message}</span>
          {retryId && (
            <Button size="sm" variant="outline" onClick={() => run(true)} disabled={busy}
              data-testid="business-search-retry" className="rounded-sm gap-1.5">
              {busy && <Loader2 size={13} className="animate-spin" />} Check again
            </Button>
          )}
        </div>
      )}

      <div className="mt-5 grid gap-3">
        {results.map((r, i) => (
          <div key={`${r.sosId || r.title}-${i}`} data-testid={`business-result-${i}`}
            className="border-2 border-black/10 hover:border-black/25 transition-colors p-5 bg-background">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Building2 size={16} className="text-accent shrink-0" />
                  <h4 className="font-semibold text-base">{r.title || "Unnamed entity"}</h4>
                  {r.status && <Badge variant="outline" className="rounded-sm text-xs">{r.status}</Badge>}
                </div>
                <div className="mt-2 text-sm text-muted-foreground space-y-0.5">
                  {r.entityType && <p>{r.entityType}{r.sosId ? ` · SOS ID ${r.sosId}` : ""}</p>}
                  {addressOf(r) && <p className="flex items-start gap-1.5"><MapPin size={13} className="mt-0.5 shrink-0" /> {addressOf(r)}</p>}
                  {r.agentName && <p>Registered agent: <span className="text-foreground">{r.agentName}</span>{agentAddressOf(r) ? ` — ${agentAddressOf(r)}` : ""}</p>}
                  {r.filingDate && <p>Filed: {r.filingDate}</p>}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                {onSelect && (
                  <Button size="sm" onClick={() => onSelect({
                    entity_name: r.title || "", registered_agent: r.agentName || "", sos_id: r.sosId || "",
                    state: r.stateOfSosRegistration || stateCode, status: r.status || "",
                    entity_type: r.entityType || "", address: addressOf(r),
                  })} data-testid={`business-select-${i}`}
                    className="rounded-sm gap-1.5 bg-primary hover:bg-accent hover:text-accent-foreground transition-colors">
                    <CheckCircle2 size={14} /> Use this business
                  </Button>
                )}
                {r.url && <a href={r.url} target="_blank" rel="noreferrer" className="text-xs text-accent hover:underline inline-flex items-center gap-1">SOS record <ExternalLink size={11} /></a>}
              </div>
            </div>
          </div>
        ))}
        {searched && !busy && !results.length && !message && (
          <p className="text-sm text-muted-foreground">No results.</p>
        )}
      </div>
    </div>
  );
}
